import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { collections, db, storageBucket } from "../firebase-config";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  startAfter,
  updateDoc,
} from "firebase/firestore";
import moment from "moment";
import { removeNullKeys } from "./helpers";
import { query, where } from "firebase/firestore";
import { getCachedData } from "./cache";
import Compressor from "compressorjs";

export const getFileURL = (file, folder, name) => {
  return new Promise((resolve, reject) => {
    console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);

    new Compressor(file, {
      quality: 0.6, // Adjust compression quality (0 to 1)
      maxWidth: 1200, // Optional: limit dimensions
      success: async (compressedFile) => {
        try {
          console.log(`Compressed file size: ${(compressedFile.size / 1024).toFixed(2)} KB`);

          const uniqueID = Date.now() + Math.floor(Math.random() * 10000).toString();
          const fileRef = ref(storageBucket, `/${folder}/${uniqueID}-${name}`);

          // Upload with Cache-Control metadata for long browser caching
          await uploadBytes(fileRef, compressedFile, {
            cacheControl: 'public,max-age=604800', // 1 year caching
          });

          const url = await getDownloadURL(fileRef);
          resolve(url);
        } catch (err) {
          reject(`Upload failed: ${err.message}`);
        }
      },
      error(err) {
        reject(`Compression failed: ${err.message}`);
      },
    });
  });
};

export const getDataById = async (table, documentId) => {
  const documentRef = doc(db, table, documentId);

  try {
    const document = await getDoc(documentRef);

    if (document.exists()) {
      return { ...document.data(), info: { id: document.id, table } };
    } else {
      throw new Error(`No document found with ID: ${documentId}`);
    }
  } catch (err) {
    return err;
  }
};

export const getDataByDateRange = async (table, startDate, endDate) => {
  let collectionRef = collection(db, table);

  try {
    // Fetch data from Firestore
    const data = await getDocs(collectionRef);
    const actualData = data.docs;
    const newData = [];

    for (let i = 0; i < actualData.length; i++) {
      const doc = actualData[i];
      let datas = { ...doc.data() };

      newData.push({ ...datas, info: { id: doc.id, table } });
    }

    // Apply date filtering if both startDate and endDate are provided
    if (startDate && endDate) {
      // Convert startDate & endDate to timestamps
      const start = new Date(startDate + "T00:00:00Z").getTime(); // Start of the day
      const end = new Date(endDate + "T23:59:59Z").getTime(); // End of the day

      return newData.filter((item) => {
        // Convert createdAt string to timestamp
        const itemDate = item?.createdAt
          ? new Date(item.createdAt).getTime()
          : null;
        return itemDate && itemDate >= start && itemDate <= end;
      });
    }

    return newData;
  } catch (err) {
    return err;
  }
};

export const getDataByUserId = async (table, userId) => {
  const tableRef = collection(db, table);
  const q = query(tableRef, where("user", "==", userId));

  try {
    const querySnapshot = await getDocs(q);

    const data = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return data; // returns [] if no data found — safe for .filter()
  } catch (err) {
    console.error("Error fetching data:", err);
    return []; // Always return array to avoid runtime errors
  }
};


export const getByUserId = async (table, userId) => {
  const tableRef = collection(db, table); // Dynamic table name
  const querySnapshot = await getDocs(tableRef);
  const data = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  const fetchdata = data.filter((f) => f.id === userId);
  return fetchdata;
};

// utils/services.js (updated getData)
export const getData = async (table, userId) => {
  try {
    let collectionRef = collection(db, table);
    const data = await getDocs(collectionRef);

    const actualData = data.docs;
    const newData = [];

    for (let i = 0; i < actualData.length; i++) {
      const doc = actualData[i];
      const hasUser = doc.data()?.user;
      let datas = { ...doc.data() };

      if (hasUser) {
        const userData = await getCachedData(collections.USERS, () =>
          getData(collections.USERS)
        );

        datas["user"] = userData?.find((users) => {
          return users.info.id === hasUser;
        });
      }

      newData.push({ ...datas, info: { id: doc.id, table: table } });
    }

    return userId
      ? newData.filter((items) => items?.user?.info?.id === userId)
      : newData;
  } catch (err) {
    console.error("Error fetching data:", err);
    throw err;
  }
};


const VALID_TYPES = [
  "sareeshapers",
  "readyblowses",
  "coordset",
  "maternitygown",
  "thignshaper",
  "slimshaper",
  "maxishaper",
  // "maxishorts",
  "Unstitchedsalwarset",
];

// In-memory cache (module-scoped, lasts during app session/runtime)

export const getDatas = async (
  table,
  { limitCount = 20, lastDoc = null, type = null } = {}
) => {
  try {
    // ---------- Build query constraints ----------
    const constraints = [];

    // Apply type filter only if it is in the whitelist
    if (type && type !== "allproducts" && VALID_TYPES.includes(type)) {
      constraints.push(where("type", "==", type));
    }

    constraints.push(orderBy("createdAt", "desc"));

    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    constraints.push(limit(limitCount));

    // ---------- Run the query ----------
    const snap = await getDocs(query(collection(db, table), ...constraints));

    // ---------- Transform snapshot ----------
    const data = snap.docs.map((doc) => ({
      ...doc.data(),                  // all product fields
      info: { id: doc.id, table },   // keep your existing {info.id, info.table} helper
    }));

    // Cursor for the next page
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

    return { data, lastDoc: nextCursor };
  } catch (err) {
    console.error("Error fetching products:", err);
    throw err;
  }
};
export const getPaginatedOrders = async ({
  table,
  pageSize = 100,
  lastVisibleDoc = null,
  fromDate = null,
  toDate = null,
}) => {
  try {
    const constraints = [];

    // 🕒 Handle date filtering using Firestore Timestamps
    if (fromDate && toDate) {
      // Use ISO string format for start and end of day
      const from = new Date(fromDate).toISOString(); // e.g. '2025-04-30T00:00:00.000Z'
      const toDateObj = new Date(toDate);
      toDateObj.setHours(23, 59, 59, 999);
      const to = toDateObj.toISOString(); // e.g. '2025-04-30T23:59:59.999Z'

      constraints.push(where("orderDate", ">=", from));
      constraints.push(where("orderDate", "<=", to));
    }

    // ❌ Exclude 'PAYMENTING' status
    // constraints.push(where("status", "!=", "PAYMENTING"));

    // ✅ Required orderBy calls (FireStore requirement when using inequality filter)
    // constraints.push(orderBy("status"));
    constraints.push(orderBy("orderDate", "desc"));

    // 🧭 Pagination
    if (lastVisibleDoc) {
      constraints.push(startAfter(lastVisibleDoc));
    }

    // ⏱️ Limit results
    constraints.push(limit(pageSize));

    // 🔍 Execute the query
    const q = query(collection(db, table), ...constraints);
    const snapshot = await getDocs(q);
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];

    const orders = snapshot.docs.map((doc) => ({
      ...doc.data(),
      info: { id: doc.id, table },
    }));

    return { orders, lastVisibleDoc: lastDoc };
  } catch (err) {
    console.error("Error fetching paginated orders:", err);
    throw err;
  }
};

// export const getPaginatedOrders = async ({
//   table,
//   pageSize = 100,
//   lastVisibleDoc = null,
//   fromDate = null,
//   toDate = null,
// }) => {
//   try {
//     const constraints = [];

//     // Date filtering with ISO strings (since orderDate is stored as string)
//     if (fromDate && toDate) {
//       const from = new Date(fromDate).toISOString();
//       const toDateObj = new Date(toDate);
//       toDateObj.setHours(23, 59, 59, 999);
//       const to = toDateObj.toISOString();

//       constraints.push(where("orderDate", ">=", from));
//       constraints.push(where("orderDate", "<=", to));
//     }

//     // Conditionally exclude 'PAYMENTING' on production only
//     if (process.env.NODE_ENV === 'production') {
//       constraints.push(where("status", "!=", "PAYMENTING"));
//     }

//     // Firestore requires orderBy on fields used in inequality filters
//     constraints.push(orderBy("status"));
//     constraints.push(orderBy("orderDate", "desc"));

//     if (lastVisibleDoc) {
//       constraints.push(startAfter(lastVisibleDoc));
//     }

//     constraints.push(limit(pageSize));

//     const q = query(collection(db, table), ...constraints);
//     const snapshot = await getDocs(q);
//     const lastDoc = snapshot.docs[snapshot.docs.length - 1];

//     const orders = snapshot.docs.map((doc) => ({
//       ...doc.data(),
//       info: { id: doc.id, table },
//     }));

//     return { orders, lastVisibleDoc: lastDoc };
//   } catch (err) {
//     console.error("Error fetching paginated orders:", err);
//     throw err;
//   }
// };

export const addData = async (table, data, userId) => {
  let dataRef = collection(db, table);

  try {
    const response = await addDoc(
      dataRef,
      removeNullKeys({
        ...data,
        createdAt: moment().utc().format(),
        user: userId,
      })
    );
    return response;
  } catch (err) {
    return err;
  }
};

export const updateData = async (table, data, docId) => {
  let dataRef = doc(db, table, docId);

  try {
    await updateDoc(dataRef, { ...data, lastUpdated: moment().utc().format() });
  } catch (err) {
    return err;
  }
};

export const deleteData = async (table, docId) => {
  const dataRef = doc(db, table, docId);

  try {
    const docSnap = await getDoc(dataRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const imageUrls = data.images || [];

      const deletePromises = imageUrls.map((url) => {
        // Decode and extract file path from the URL
        const decodedUrl = decodeURIComponent(url);
        const start = decodedUrl.indexOf("/o/") + 3;
        const end = decodedUrl.indexOf("?alt=");
        const fullPath = decodedUrl.substring(start, end); // e.g. "productImage/17525821779041755-e.jpg"

        const imageRef = ref(storageBucket, fullPath);
        return deleteObject(imageRef);
      });

      await Promise.all(deletePromises);
    }

    // Finally, delete the Firestore document
    await deleteDoc(dataRef);
    console.log("Document and images deleted successfully.");
  } catch (error) {
    console.error("Error deleting data or images:", error);
    return error;
  }
};

// export const deleteDataByUserId = async (table, userId) => {
//   const q = query(collection(db, table), where("user", "==", userId)); // <-- Fixed here

//   try {
//     const querySnapshot = await getDocs(q);
//     if (querySnapshot.empty) {
//       return `No document found with user: ${userId}`;
//     }

//     const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref));
//     await Promise.all(deletePromises);

//     return "Document(s) deleted successfully.";
//   } catch (err) {
//     return err.message;
//   }
// };

export const deleteDataByUserId = async (collectionName, userId) => {
  // Create a query to get all cart items where 'user' field matches the user's document ID
  const q = query(collection(db, collectionName), where("user", "==", userId));

  try {
    const querySnapshot = await getDocs(q);

    console.log(`Deleting cart items for userId: ${userId}`);
    console.log(`Found ${querySnapshot.size} items in cart.`);

    if (querySnapshot.empty) {
      return `No cart items found for user: ${userId}`;
    }

    // Delete all documents that match the query
    const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    return "Cart items deleted successfully.";
  } catch (err) {
    console.error("Error deleting cart items:", err);
    return `Error: ${err.message}`;
  }
};
export const getDocument = async (table, documentId, userId) => {
  let documentRef = doc(db, table, documentId);

  try {
    const data = await getDoc(documentRef);
    const newData = data.data()
      ? { ...data.data(), info: { id: data.id, table: table } }
      : [];

    if (userId) {
      return newData.filter((items) => {
        return items?.user === userId;
      });
    } else {
      return newData;
    }
  } catch (err) {
    return err;
  }
};

export const getOrderByPaymentId = async (table, paymentId) => {
  const tableRef = collection(db, table);
  const q = query(tableRef, where("paymentId", "==", paymentId));

  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null; // no order found
    }

    // usually one order per paymentId
    const docSnap = querySnapshot.docs[0];
    return {
      ...docSnap.data(),
      info: { id: docSnap.id, table },
    };
  } catch (err) {
    console.error("Error fetching data by paymentId:", err);
    return null;
  }
};
